import { useEffect, useRef, useState } from 'react'
import { formatCurrency, formatDateLong } from './booking/helpers'
import type { BookingSummary, ServiceCard } from '../../types'
import { useMessages } from '../../state/MessageProvider'
import { ChevronDown, ChevronUp } from 'lucide-react'

type ChatTranscriptProps = {
  onStartBooking?: (serviceId?: string) => void
  onStartInquiry?: () => void
  phoneNumber?: string
}

const SummaryCard = ({ summary }: { summary: BookingSummary }) => (
  <div className="mt-2 w-full max-w-[85%] rounded-lg border border-slate-200/60 bg-slate-50 p-3 text-left text-sm text-slate-700">
    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
      Appointment summary
    </p>
    <p className="mt-1 text-base font-semibold text-slate-900">
      {summary.service.name} · {summary.service.durationMinutes} min
    </p>
    <p className="text-xs text-slate-500">
      {formatDateLong(summary.selection.date)} · {summary.selection.slot}
    </p>
    <p className="mt-1 text-sm font-semibold text-slate-900">
      {formatCurrency(summary.payment.amountCents)}
    </p>
    <p className="text-xs text-slate-500">
      Receipt sent to <strong>{summary.form.email}</strong>
    </p>
  </div>
)

// CollapsibleSection previously supported extra info; retained for potential reuse
const CollapsibleSection = ({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border-t border-slate-200/60 mt-2">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-2 text-left hover:bg-slate-50/50 transition-colors"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-600">
          {title}
        </span>
        {isOpen ? (
          <ChevronUp className="w-3 h-3 text-slate-500" />
        ) : (
          <ChevronDown className="w-3 h-3 text-slate-500" />
        )}
      </button>
      {isOpen && (
        <div className="pb-2 text-[11px] text-slate-700 leading-relaxed">
          {children}
        </div>
      )}
    </div>
  )
}

const ServiceCardDisplay = ({ card, onServiceClick }: { card: ServiceCard; onServiceClick?: (serviceId: string) => void }) => {
  const CARDS_PER_PAGE = 2
  const [currentPage, setCurrentPage] = useState(0)

  const totalPages = Math.ceil(card.services.length / CARDS_PER_PAGE)
  const startIndex = currentPage * CARDS_PER_PAGE
  const visibleServices = card.services.slice(startIndex, startIndex + CARDS_PER_PAGE)
  const canGoBack = currentPage > 0
  const canGoForward = currentPage < totalPages - 1

  const handleNext = () => {
    if (canGoForward) {
      setCurrentPage((prev) => prev + 1)
    }
  }

  const handlePrevious = () => {
    if (canGoBack) {
      setCurrentPage((prev) => prev - 1)
    }
  }

  return (
    <div className="mt-2 mb-2 w-full max-w-[95%]">
      {card.title && (
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{card.title}</p>
      )}
      <div className="relative">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={!canGoBack}
            aria-label="Previous services"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200/60 bg-white text-slate-700 transition hover:border-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ←
          </button>

          <div className="flex flex-1 gap-2 overflow-hidden">
            {visibleServices.map((service) => (
              <button
                type="button"
                key={service.id ?? service.name}
                onClick={() => service.id && onServiceClick?.(service.id)}
                className="flex-1 min-w-0 rounded-lg border border-slate-200/70 bg-white p-2 shadow-sm hover:shadow-md hover:border-slate-300 hover:scale-[1.02] transition-all cursor-pointer text-left"
              >
                <div className="mb-1">
                  <h3 className="text-[11px] font-semibold text-slate-900 leading-tight line-clamp-2 mb-0.5">
                    {service.name}
                  </h3>
                  <span className="text-xs font-bold text-slate-900">
                    {service.price}
                  </span>
                </div>
                <p className="text-[10px] text-slate-600 leading-snug mb-1.5 line-clamp-2">
                  {service.description}
                </p>
                <div className="flex items-center gap-1 text-[8px] uppercase tracking-wide text-slate-500">
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" className="text-slate-400">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5"/>
                    <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                  {service.duration}
                </div>
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={handleNext}
            disabled={!canGoForward}
            aria-label="Next services"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200/60 bg-white text-slate-700 transition hover:border-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            →
          </button>
        </div>

        {totalPages > 1 && (
          <p className="mt-1 text-center text-[9px] text-slate-500">
            {currentPage + 1} / {totalPages}
          </p>
        )}

      </div>
    </div>
  )
}

export const ChatTranscript = ({ onStartBooking, onStartInquiry, phoneNumber }: ChatTranscriptProps) => {
  const { messages } = useMessages()
  const lastMessageRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (!lastMessageRef.current) return
    lastMessageRef.current.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const formatMessage = (content: string) => {
    // Split by double newlines to create paragraphs
    return content.split('\n\n').map((paragraph, index) => (
      <span key={index} className="block mb-2 last:mb-0">
        {paragraph.split('\n').map((line, lineIndex) => (
          <span key={lineIndex}>
            {line}
            {lineIndex < paragraph.split('\n').length - 1 && <br />}
          </span>
        ))}
      </span>
    ))
  }

  return (
    <div className="mb-2 py-1 px-2.5">
      <ul className="flex flex-col gap-1.5 overflow-y-auto pr-1.5 text-sm">
        {messages.map((message, index) => {
          const isUser = message.sender === 'user'
          const isLastMessage = index === messages.length - 1
          return (
            <li
              key={message.id}
              ref={isLastMessage ? lastMessageRef : null}
              className={`flex ${isUser ? 'justify-end' : 'justify-start'} flex-col ${isUser ? 'items-end' : 'items-start'}`}
            >
              {!isUser && message.serviceCard && (
                <ServiceCardDisplay
                  card={message.serviceCard}
                  onServiceClick={(serviceId) => onStartBooking?.(serviceId)}
                />
              )}
              {message.content && message.content.trim() && (
                <span
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${
                    isUser
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-900'
                  }`}
                >
                  {isUser ? message.content : formatMessage(message.content)}
                </span>
              )}
              {!isUser && message.showBookingButton && onStartBooking && (
                <button
                  type="button"
                  onClick={() => onStartBooking()}
                  className="mt-2 flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:opacity-90 hover:shadow"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M8 3v4M16 3v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  Book Appointment
                </button>
              )}
              {!isUser && message.showServicesButton && (
                <button
                  type="button"
                  onClick={() => {
                    const targetUrl = message.servicesManageUrl || '/dashboard/services'
                    if (typeof window !== 'undefined') {
                      window.open(targetUrl, '_blank')
                    }
                  }}
                  className="mt-2 flex items-center gap-2 rounded-lg border border-slate-200/60 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M5 7h14v10H5V7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                    <path d="M9 5h6v2H9V5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                    <path d="M9 11h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M9 13h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Go to Services
                </button>
              )}
              {!isUser && message.showInquiryButton && onStartInquiry && (
                <button
                  type="button"
                  onClick={onStartInquiry}
                  className="mt-2 flex items-center gap-2 rounded-lg border border-slate-200/60 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 6h16v12H4V6zm0-2a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2H4z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path d="M4 8l8 5 8-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <span className="flex flex-col items-start">
                    <span>
                      Leave a message
                      {phoneNumber && (
                        <span className="ml-1 text-[11px] font-normal text-slate-500">
                          · {phoneNumber}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              )}
              {!isUser && message.summary && <SummaryCard summary={message.summary} />}
            </li>
          )
        })}
        {messages.length === 0 && (
          <li className="py-6 text-center text-xs text-slate-400">No messages yet.</li>
        )}
      </ul>
    </div>
  )
}
